/**
 * Wishlist V2 Controller
 *
 * Core JavaScript controller for the Includ Wishlist V2 feature.
 * Handles API calls (logged-in users), guest localStorage fallback,
 * optimistic UI toggling, badge sync, toast notifications, and
 * hydration of wishlist buttons across the storefront.
 *
 * ES5-compatible — no arrow functions, const/let, or template literals.
 */
(function() {
  'use strict';

  // ─── Double-init guard ───────────────────────────────────────────────────────
  if (window.__wishlistV2Init) return;
  window.__wishlistV2Init = true;

  // ─── Constants ───────────────────────────────────────────────────────────────
  var GUEST_KEY = '__wishlist_v2_guest';
  var BADGE_CAP = 99;

  // ─── Config (set by Liquid header snippet) ───────────────────────────────────
  // Expected shape: { proxyBase: string, isLoggedIn: boolean }
  var config = window.__wishlistV2Config || null;

  // ─── State ───────────────────────────────────────────────────────────────────
  var wishlistedIds = new Set();

  /**
   * Returns true if the current visitor is a logged-in customer.
   */
  function isLoggedIn() {
    return !!(config && config.isLoggedIn === true);
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // 1. API CALLS
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Add a product to the wishlist via the app proxy.
   * Sends JSON body so that Spring does not merge body params into the
   * query-parameter map (which would break Shopify signature verification).
   * @param {string} productId
   * @returns {Promise<{ok: boolean}>}
   */
  function addToWishlist(productId) {
    var url = config.proxyBase + '/v1/wishlist/add';

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ product_id: productId, action: 'ADD' })
    }).then(function(response) {
      return { ok: response.ok };
    }).catch(function() {
      return { ok: false };
    });
  }

  /**
   * Remove a product from the wishlist via the app proxy.
   * Sends JSON body so that Spring does not merge body params into the
   * query-parameter map (which would break Shopify signature verification).
   * @param {string} productId
   * @returns {Promise<{ok: boolean}>}
   */
  function removeFromWishlist(productId) {
    var url = config.proxyBase + '/v1/wishlist/add';

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ product_id: productId, action: 'REMOVE' })
    }).then(function(response) {
      return { ok: response.ok };
    }).catch(function() {
      return { ok: false };
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // 2. GUEST STORAGE
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Read guest wishlist items from localStorage.
   * @returns {string[]} Array of product ID strings.
   */
  function getGuestItems() {
    try {
      var raw = localStorage.getItem(GUEST_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Write guest wishlist items to localStorage.
   * @param {string[]} items
   * @returns {boolean} False when the browser refused the write (storage full or
   *   switched off), so the caller can undo the heart instead of showing a save
   *   that is gone on the next page.
   */
  function setGuestItems(items) {
    try {
      localStorage.setItem(GUEST_KEY, JSON.stringify(items));
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Add a product ID to the guest wishlist if not already present.
   * @param {string} productId
   * @returns {boolean} Whether the list was saved.
   */
  function addGuestItem(productId) {
    var items = getGuestItems();
    var id = String(productId);
    if (items.indexOf(id) !== -1) return true;
    items.push(id);
    return setGuestItems(items);
  }

  /**
   * Remove a product ID from the guest wishlist.
   * @param {string} productId
   * @returns {boolean} Whether the list was saved.
   */
  function removeGuestItem(productId) {
    var items = getGuestItems();
    var id = String(productId);
    var index = items.indexOf(id);
    if (index === -1) return true;
    items.splice(index, 1);
    return setGuestItems(items);
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // 2b. LOCALSTORAGE BRIDGE (logged-in users)
  // ═══════════════════════════════════════════════════════════════════════════════
  //
  // WHY THIS EXISTS:
  // For logged-in customers the wishlist lives in the Shopify customer metafield
  // `custom.customer_wishlist`, which the theme reads SERVER-SIDE at Liquid render
  // time. Shopify's storefront read of customer metafields lags behind the write
  // performed by our backend — so right after a user adds/removes a product, the
  // next page render can still return the OLD list. Symptoms: the PDP/card heart
  // loses its filled state on navigation, and freshly added products are missing
  // from the wishlist page even though the metafield is correct in admin.
  //
  // This bridge keeps a short-lived localStorage record of the user's most recent
  // intent and overlays it on the stale server list until the metafield catches
  // up, at which point the record self-heals (is dropped by reconcile()).
  //
  // Guests are unaffected — they already persist to localStorage via GUEST_KEY.

  var PENDING_KEY = '__wishlist_v2_pending';
  var PENDING_TTL = 10 * 60 * 1000; // 10 minutes — ample time for propagation

  /**
   * Read the pending-changes map, pruning entries older than the TTL.
   * @returns {Object} Map of productId -> { a: 'added'|'removed', t: timestamp }
   */
  function getPendingChanges() {
    var out = {};
    try {
      var raw = localStorage.getItem(PENDING_KEY);
      if (!raw) return out;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return out;

      var now = Date.now();
      var pruned = false;
      for (var id in parsed) {
        if (!parsed.hasOwnProperty(id)) continue;
        var entry = parsed[id];
        if (!entry || typeof entry.t !== 'number') { pruned = true; continue; }
        if (now - entry.t > PENDING_TTL) { pruned = true; continue; }
        out[id] = entry;
      }
      if (pruned) writePendingChanges(out);
    } catch (e) {}
    return out;
  }

  /**
   * Persist the pending-changes map (removes the key entirely when empty).
   * @param {Object} map
   */
  function writePendingChanges(map) {
    try {
      var empty = true;
      for (var k in map) { if (map.hasOwnProperty(k)) { empty = false; break; } }
      if (empty) {
        localStorage.removeItem(PENDING_KEY);
      } else {
        localStorage.setItem(PENDING_KEY, JSON.stringify(map));
      }
    } catch (e) {}
  }

  /**
   * Record that the user just added or removed a product.
   * @param {string} productId
   * @param {string} action - 'added' or 'removed'
   */
  function setPendingChange(productId, action) {
    var map = getPendingChanges();
    map[String(productId)] = { a: action, t: Date.now() };
    writePendingChanges(map);
  }

  /**
   * Forget a pending change — used when the API write failed, so the overlay
   * never reports a state that was not actually persisted.
   * @param {string} productId
   */
  function clearPendingChange(productId) {
    var map = getPendingChanges();
    var id = String(productId);
    if (map.hasOwnProperty(id)) {
      delete map[id];
      writePendingChanges(map);
    }
  }

  /**
   * Drop pending entries the server data already agrees with, so we stop
   * overriding once the metafield has propagated.
   * @param {string[]} serverIds
   */
  function reconcilePendingChanges(serverIds) {
    var map = getPendingChanges();
    var onServer = {};
    for (var i = 0; i < serverIds.length; i++) onServer[String(serverIds[i])] = true;

    var changed = false;
    for (var id in map) {
      if (!map.hasOwnProperty(id)) continue;
      var present = !!onServer[id];
      if ((map[id].a === 'added' && present) || (map[id].a === 'removed' && !present)) {
        delete map[id];
        changed = true;
      }
    }
    if (changed) writePendingChanges(map);
  }

  /**
   * Merge server-provided IDs with pending changes to get the effective list.
   * @param {string[]} serverIds
   * @returns {string[]} Effective product IDs
   */
  function mergeWithPending(serverIds) {
    var map = getPendingChanges();
    var result = [];
    var seen = {};

    for (var i = 0; i < serverIds.length; i++) {
      var sid = String(serverIds[i]);
      if (map[sid] && map[sid].a === 'removed') continue; // just removed — hide
      if (seen[sid]) continue;
      seen[sid] = true;
      result.push(sid);
    }

    // Append just-added items the server hasn't caught up on yet.
    for (var id in map) {
      if (!map.hasOwnProperty(id)) continue;
      if (map[id].a !== 'added') continue;
      if (seen[id]) continue;
      seen[id] = true;
      result.push(id);
    }

    return result;
  }

  /**
   * Read the server-rendered wishlist IDs and return the effective list with
   * pending changes applied. Single entry point for all consumers.
   * @returns {string[]}
   */
  function getEffectiveServerIds() {
    var serverIds = [];
    var dataScript = document.getElementById('wishlist-v2-initial-data')
                  || document.getElementById('wishlist-v2-page-data');
    if (dataScript) {
      try {
        var ids = JSON.parse(dataScript.textContent);
        if (Array.isArray(ids)) {
          for (var i = 0; i < ids.length; i++) serverIds.push(String(ids[i]));
        }
      } catch (e) {}
    }
    reconcilePendingChanges(serverIds);
    return mergeWithPending(serverIds);
  }

  // Exposed so the wishlist page section and cart-drawer carousel — which read
  // the server-rendered list independently of this controller — go through the
  // same bridge instead of trusting the stale metafield directly.
  window.WishlistV2Pending = {
    get: getPendingChanges,
    set: setPendingChange,
    clear: clearPendingChange,
    reconcile: reconcilePendingChanges,
    merge: mergeWithPending,
    effectiveServerIds: getEffectiveServerIds
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // 3. TOAST NOTIFICATIONS
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * The theme's standard toast (snippets/includ-toast.liquid).
   * @param {string} message
   * @param {'error'} [type]
   */
  function showToast(message, type) {
    if (window.includToast) window.includToast(message, { type: type });
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // 4. BADGE SYNC
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Update all wishlist badge elements with the current count.
   */
  function updateBadge() {
    var badges = document.querySelectorAll('.includ-wl-count');
    var count = wishlistedIds.size;
    var displayCount = count > BADGE_CAP ? '99+' : String(count);

    for (var i = 0; i < badges.length; i++) {
      var badge = badges[i];
      if (count > 0) {
        badge.textContent = displayCount;
        badge.style.display = '';
        badge.removeAttribute('hidden');
      } else {
        badge.textContent = '';
        badge.style.display = 'none';
      }
    }
  }

  // Listen for cross-component sync
  document.addEventListener('wishlist:v2:updated', function(e) {
    var detail = e.detail || {};
    if (detail.productId) {
      if (detail.action === 'added') {
        wishlistedIds.add(String(detail.productId));
      } else if (detail.action === 'removed' || detail.action === 'remove') {
        wishlistedIds.delete(String(detail.productId));
      }
    }
    if (detail.action === 'remove_all') {
      wishlistedIds.clear();
    }
    updateBadge();
    hydrateButtons();
  });

  // Listen for QuickShop add-to-cart — auto-remove from wishlist (Move to Bag flow)
  document.addEventListener('quickshop:added-to-cart', function(e) {
    var detail = e.detail || {};
    var productId = String(detail.productId || '');
    if (!productId || !wishlistedIds.has(productId)) return;

    // Optimistic local update so the badge/buttons reflect the change immediately
    wishlistedIds.delete(productId);
    updateBadge();
    hydrateButtons();

    // Announce as a distinct action so analytics can separate "moved to bag"
    // from a plain user-initiated removal.
    document.dispatchEvent(new CustomEvent('wishlist:v2:updated', {
      bubbles: true,
      detail: {
        action: 'move_to_bag',
        productId: productId,
        count: wishlistedIds.size
      }
    }));

    if (isLoggedIn()) {
      setPendingChange(productId, 'removed');
      removeFromWishlist(productId).then(function(result) {
        if (!result.ok) {
          // Revert on failure
          clearPendingChange(productId);
          wishlistedIds.add(productId);
          updateBadge();
          hydrateButtons();
        }
      });
    } else {
      removeGuestItem(productId);
    }
  });

  // ═══════════════════════════════════════════════════════════════════════════════
  // 5. CLICK HANDLER (capture phase, delegated)
  // ═══════════════════════════════════════════════════════════════════════════════

  document.addEventListener('click', function(e) {
    // Find the wishlist button via delegation
    var el = e.target;
    while (el && el !== document) {
      if (el.hasAttribute && el.hasAttribute('data-wishlist-v2-btn')) break;
      el = el.parentElement;
    }
    if (!el || el === document) return;

    // Bail if already processing
    if (el.classList.contains('is-processing')) return;

    e.preventDefault();
    e.stopPropagation();

    var productId = String(el.dataset.productId || el.getAttribute('data-product-id'));
    if (!productId) return;

    // Determine action based on current state
    var isCurrentlyWishlisted = el.classList.contains('is-wishlisted');
    var action = isCurrentlyWishlisted ? 'removed' : 'added';

    el.classList.add('is-processing');

    // Optimistic toggle, persistence (API or guest localStorage), revert-on-error
    // and the `wishlist:v2:updated` broadcast all live in persistChange() — see
    // section 8. It flips EVERY button for this product via hydrateButtons(), so
    // there's no per-button loop here.
    var promise = persistChange(productId, action);

    showToast(action === 'added' ? 'Added to wishlist' : 'Removed from wishlist');

    // Clear the loading state immediately. The heart is already toggled
    // optimistically and (for logged-in users) the intent is recorded in the
    // localStorage bridge, so there's no reason to keep the spinner up while
    // /v1/wishlist/add completes. The call runs in the background and
    // persistChange reverts only if it fails.
    el.classList.remove('is-processing');

    promise.then(function(result) {
      if (!result.ok) {
        showToast('Could not update your wishlist. Please try again.', 'error');
      }
    });
  }, true); // capture phase

  // ═══════════════════════════════════════════════════════════════════════════════
  // 6. HYDRATION
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Hydrate all wishlist buttons on the page — toggle .is-wishlisted
   * based on the current wishlistedIds set.
   */
  function hydrateButtons() {
    var buttons = document.querySelectorAll('[data-wishlist-v2-btn]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var pid = String(btn.dataset.productId || btn.getAttribute('data-product-id'));
      if (wishlistedIds.has(pid)) {
        btn.classList.add('is-wishlisted');
      } else {
        btn.classList.remove('is-wishlisted');
      }
    }
  }

  /**
   * Main initialization — load initial data and hydrate UI.
   */
  function init() {
    // Populate wishlistedIds from server data or guest storage
    if (isLoggedIn()) {
      // Read through the localStorage bridge rather than trusting the
      // server-rendered metafield directly — it may not reflect writes made
      // moments ago. getEffectiveServerIds() parses the server data, discards
      // pending records the server has caught up on, then overlays the rest.
      var effectiveIds = getEffectiveServerIds();
      for (var i = 0; i < effectiveIds.length; i++) {
        wishlistedIds.add(effectiveIds[i]);
      }
      // Must run after wishlistedIds holds the server list, so items already
      // on the account are no-ops instead of a second write.
      mergeGuestItems();
    } else {
      // Guest: load from localStorage
      var guestItems = getGuestItems();
      for (var j = 0; j < guestItems.length; j++) {
        wishlistedIds.add(String(guestItems[j]));
      }
    }

    // Hydrate buttons and badge
    hydrateButtons();
    updateBadge();
  }

  /**
   * Carry a guest's hearts into the account after sign-in.
   *
   * WHY THIS EXISTS:
   * Hearts tapped while signed out live in localStorage (GUEST_KEY). Once the
   * visitor signs in, init() reads only the server list, so without this those
   * hearts silently vanished — breaking the sign-in prompt's "keep your
   * wishlist on any phone" promise. (Spectrum's own login merge in
   * spectrum-sdk.js reads its V1 key, never GUEST_KEY.)
   *
   * Each item goes through persistChange(), so the server write, the pending
   * bridge and revert-on-error behave exactly as for a tap. An item is dropped
   * from GUEST_KEY only once its write succeeded (or it was already on the
   * account), so a failed write retries on the next page load.
   *
   * Silent: these hearts were already announced when the guest tapped them, so
   * re-broadcasting would double-count GA `wishlist_added` and buzz the app's
   * haptics once per item on page load.
   */
  function mergeGuestItems() {
    // One write at a time. Parallel writes lost items: on 18 Oct (M31 app,
    // signed in after 2 guest hearts) both writes answered ok but the account
    // kept only 1, consistent with each write replacing the whole stored list.
    getGuestItems().reduce(function(chain, productId) {
      return chain.then(function() {
        return persistChange(productId, 'added', true).then(function(result) {
          if (result.ok) removeGuestItem(result.productId);
        });
      });
    }, Promise.resolve());
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // 7. RE-HYDRATION FOR DYNAMIC CONTENT
  // ═══════════════════════════════════════════════════════════════════════════════

  // Listen for Shopify theme editor section reload
  document.addEventListener('shopify:section:load', function() {
    hydrateButtons();
  });

  // Listen for custom refresh event (e.g., after AJAX pagination)
  document.addEventListener('wishlist:v2:refresh', function() {
    hydrateButtons();
  });

  // ═══════════════════════════════════════════════════════════════════════════════
  // 8. PUBLIC API
  // ═══════════════════════════════════════════════════════════════════════════════
  //
  // WHY THIS EXISTS:
  // Other components need to add/remove wishlist items without a click on a
  // `[data-wishlist-v2-btn]` heart — e.g. the cart's "remove item → Save to
  // Wishlist" sheets. Before this API existed they POSTed to the app proxy
  // themselves, which works ONLY for logged-in customers: a guest wishlist lives
  // in localStorage (GUEST_KEY) and the proxy has no customer to attribute the
  // write to, so every guest save was silently lost. persistChange() keeps that
  // guest-vs-customer decision in exactly one place.
  //
  // Callers outside this file must go through window.WishlistV2 — never fetch
  // /v1/wishlist/add directly.

  /**
   * Applies a wishlist change: optimistic in-memory + UI update, then persist to
   * the API (logged-in) or localStorage (guest), reverting if the write fails.
   *
   * Also used by the delegated heart-button handler above, so both entry points
   * share one persistence path.
   *
   * @param {string|number} rawProductId - Numeric Shopify product id.
   * @param {string} action - 'added' or 'removed'.
   * @param {boolean} [silent] - Skip the `wishlist:v2:updated` broadcast (used
   *   by mergeGuestItems() for hearts that were already announced).
   * @returns {Promise<{ok: boolean, action: string, productId: string, noop: boolean}>}
   */
  /** @type {Object<string, Promise<{ok: boolean}>>} The last API write queued for each product. */
  var writesInFlight = {};

  function persistChange(rawProductId, action, silent) {
    var productId = String(rawProductId || '');

    if (!productId || (action !== 'added' && action !== 'removed')) {
      return Promise.resolve({ ok: false, action: action, productId: productId, noop: false });
    }

    // Already in the target state: no write and no event, but still a success so
    // callers show their "saved" message. Re-sync the UI in case a button was
    // server-rendered out of step with our state.
    var isWishlisted = wishlistedIds.has(productId);
    if ((action === 'added' && isWishlisted) || (action === 'removed' && !isWishlisted)) {
      updateBadge();
      hydrateButtons();
      return Promise.resolve({ ok: true, action: action, productId: productId, noop: true });
    }

    // ── Optimistic update ──
    if (action === 'added') {
      wishlistedIds.add(productId);
    } else {
      wishlistedIds.delete(productId);
    }
    updateBadge();
    hydrateButtons();

    // ── Persist ──
    var promise;

    if (isLoggedIn()) {
      // Record the intent in the localStorage bridge so the filled/empty heart
      // survives navigation while the customer metafield catches up. Guests skip
      // this: their source of truth IS localStorage, so a pending record would be
      // duplicate state that reconcile() could never clear.
      setPendingChange(productId, action);
      // One write per product at a time, in tap order. The heart answers every
      // tap at once, but two quick taps used to send add and remove together,
      // and whichever the server finished last won.
      promise = (writesInFlight[productId] || Promise.resolve()).then(function() {
        return action === 'added'
          ? addToWishlist(productId)
          : removeFromWishlist(productId);
      });
      writesInFlight[productId] = promise;
    } else {
      // Guest: synchronous localStorage operations wrapped in a resolved promise
      var saved = action === 'added' ? addGuestItem(productId) : removeGuestItem(productId);
      promise = Promise.resolve({ ok: saved });
    }

    return promise.then(function(result) {
      if (!result.ok) {
        // ── Revert on error ──
        // Discard the recorded intent — the write never landed, so neither the
        // UI nor the bridge may keep reporting it.
        clearPendingChange(productId);
        if (action === 'added') {
          wishlistedIds.delete(productId);
        } else {
          wishlistedIds.add(productId);
        }
        updateBadge();
        hydrateButtons();
        return { ok: false, action: action, productId: productId, noop: false };
      }

      // Announce only on success. The `wishlist:v2:updated` listener above
      // re-applies detail.action to wishlistedIds, so broadcasting a failed
      // change would immediately undo the revert we just did.
      if (!silent) document.dispatchEvent(new CustomEvent('wishlist:v2:updated', {
        bubbles: true,
        detail: {
          action: action,
          productId: productId,
          count: wishlistedIds.size
        }
      }));

      return { ok: true, action: action, productId: productId, noop: false };
    });
  }

  /**
   * Imperative wishlist API for other components. Every method resolves with
   * `{ ok, action, productId, noop }` and never rejects.
   */
  window.WishlistV2 = {
    add: function(productId) {
      return persistChange(productId, 'added');
    },
    remove: function(productId) {
      return persistChange(productId, 'removed');
    },
    toggle: function(productId) {
      var next = wishlistedIds.has(String(productId)) ? 'removed' : 'added';
      return persistChange(productId, next);
    },
    has: function(productId) {
      return wishlistedIds.has(String(productId));
    },
    count: function() {
      return wishlistedIds.size;
    },
    isLoggedIn: isLoggedIn
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // 9. BOOT
  // ═══════════════════════════════════════════════════════════════════════════════

  // Run init when DOM is ready, or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
